import { Draft } from "@/types/draft";

/**
 * Validates if a draft has a valid license for publishing
 * @param draft The draft to validate
 * @returns Object with validation result and error message
 */
export function validateDraftLicense(draft: Draft): {
  isValid: boolean;
  errorMessage?: string;
} {
  // Check if draft has licensing information
  if (!draft.licensing) {
    return {
      isValid: false,
      errorMessage:
        "No license selected. Please select a license tier before publishing.",
    };
  }

  // Check if license tier is selected
  if (!draft.licensing.tier) {
    return {
      isValid: false,
      errorMessage:
        "No license tier selected. Please choose a license tier before publishing.",
    };
  }

  // Check if the tier is a valid value
  const validTiers = [
    "instant",
    "standard",
    "bespoke",
    "basic",
    "premium",
    "custom",
    "exclusive",
  ];
  if (!validTiers.includes(draft.licensing.tier)) {
    return {
      isValid: false,
      errorMessage: "Invalid license tier. Please select a valid license tier.",
    };
  }

  return {
    isValid: true,
  };
}

/**
 * Validates multiple drafts for license requirements
 * @param drafts Array of drafts to validate
 * @returns Object with validation results
 */
export function validateMultipleDraftLicenses(drafts: Draft[]): {
  validDrafts: Draft[];
  invalidDrafts: Array<{ draft: Draft; error: string }>;
  hasValidDrafts: boolean;
  hasInvalidDrafts: boolean;
} {
  const validDrafts: Draft[] = [];
  const invalidDrafts: Array<{ draft: Draft; error: string }> = [];

  drafts.forEach((draft) => {
    const validation = validateDraftLicense(draft);
    if (validation.isValid) {
      validDrafts.push(draft);
    } else {
      invalidDrafts.push({
        draft,
        error: validation.errorMessage || "Invalid license",
      });
    }
  });

  return {
    validDrafts,
    invalidDrafts,
    hasValidDrafts: validDrafts.length > 0,
    hasInvalidDrafts: invalidDrafts.length > 0,
  };
}
